import { useState, useCallback } from 'react';
import { useAuth as useAuthContext } from '../context/AuthContext';

/**
 * Custom hook for authentication operations
 * Provides methods for login, registration, logout, and password reset
 * with loading and error states
 */
const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const authContext = useAuthContext();

  /**
   * Login user with email and password
   * @param {Object} credentials - User credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} User data
   */
  const login = useCallback(async (credentials) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authContext.login(credentials);
      return result;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authContext]);

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} User data
   */
  const register = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authContext.register(userData);
      return result;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authContext]);

  /**
   * Logout the current user
   */
  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await authContext.logout();
    } catch (err) {
      setError(err.response?.data?.message || 'Logout failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authContext]);

  /**
   * Request password reset for a user
   * @param {string} email - User email
   */
  const requestPasswordReset = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      await authContext.requestPasswordReset(email);
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset request failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authContext]);

  /**
   * Reset password with token
   * @param {Object} resetData - Password reset data
   * @param {string} resetData.token - Reset token
   * @param {string} resetData.password - New password
   */
  const resetPassword = useCallback(async (resetData) => {
    setLoading(true);
    setError(null);
    
    try {
      await authContext.resetPassword(resetData);
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authContext]);

  return {
    user: authContext.user,
    isAuthenticated: authContext.isAuthenticated,
    loading,
    error,
    login,
    register,
    logout,
    requestPasswordReset,
    resetPassword,
    clearError: () => setError(null)
  };
};

export default useAuth;
